import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Lock, Loader2 } from "lucide-react";
import { useState } from "react";
import SellerLayout from "./layout";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Nuvarande lösenord krävs"),
  newPassword: z.string().min(6, "Nytt lösenord måste vara minst 6 tecken"),
  confirmPassword: z.string().min(1, "Bekräfta lösenordet"),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Lösenorden matchar inte",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export default function SellerSettings() {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const handleSubmit = async (data: PasswordFormData) => {
    setIsSaving(true);
    try {
      await apiRequest("POST", "/api/seller-auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast({ title: "Lösenord ändrat", description: "Ditt lösenord har uppdaterats." });
      form.reset();
    } catch (err: any) {
      toast({ title: "Fel", description: err?.message || "Kunde inte ändra lösenord", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SellerLayout>
      <div className="max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Inställningar</h1>
          <p className="text-muted-foreground">Hantera ditt konto</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Ändra lösenord
            </CardTitle>
            <CardDescription>Uppdatera ditt lösenord till säljportalen</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nuvarande lösenord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" data-testid="input-seller-current-password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nytt lösenord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" data-testid="input-seller-new-password" />
                      </FormControl>
                      <FormDescription>Minst 6 tecken</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bekräfta nytt lösenord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" data-testid="input-seller-confirm-password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={isSaving} data-testid="button-seller-change-password">
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Ändrar...
                    </>
                  ) : (
                    "Ändra lösenord"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
}
